import { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import ReactGA from "react-ga4";
import WelcomeModal from "./components/WelcomeModal.jsx";
import NotificationListener from "./components/NotificationListener";
import Navbar from "./components/Navbar";
import HomePage from "./pages/homepage/HomePage.jsx";
import { Toaster } from "react-hot-toast";
import LoadingSpinner from "./components/LoadingSpinner.jsx";
import WhatsAppButton from "./components/contact/WhatsAppButton.jsx";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "./store/useAuthStore.js";
import Footer from "./pages/Footer.jsx";
import { HelmetProvider } from "react-helmet-async";
import { ScrollToTop } from "./utils/ScrollToTop.js";

// Route-level code splitting: each page below becomes its own chunk,
// fetched only when a user actually navigates there instead of all
// being bundled into the initial load.
const AboutPage = lazy(() => import("./pages/about-us/AboutPage.jsx"));
const LoginPage = lazy(() => import("./pages/LoginPage.jsx"));
const SignUpPage = lazy(() => import("./pages/SignUpPage.jsx"));
const NoResults = lazy(() => import("./pages/results/NoResultPage.jsx"));
const ResultPage = lazy(() => import("./pages/results/ResultPage.jsx"));
const HotelPage = lazy(() => import("./pages/hotel/HotelPage.jsx"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard.jsx"));
const OwnerDashboard = lazy(() => import("./pages/owner/OwnerDashboard.jsx"));
const PurchaseSuccessPage = lazy(() => import("./pages/PurchaseSuccessPage.jsx"));
const PurchaseCancelPage = lazy(() => import("./pages/PurchaseCancelPage.jsx"));
const PurchaseErrorPage = lazy(() => import("./pages/PurchaseErrorPage.jsx"));
const SummaryPage = lazy(() => import("./pages/hotel/SummaryPage.jsx"));
const ReservationExpired = lazy(() => import("./pages/hotel/ReservationExpired.jsx"));
const HelpCenterPage = lazy(() => import("./pages/help-center/HelpCentrePage.jsx"));
const AccountPage = lazy(() => import("./pages/user/AccountPage.jsx"));
const MessagesPage = lazy(() => import("./pages/Messages/MessagesPage.jsx"));
const EmailVerificationPage = lazy(() => import("./pages/verification/EmailVerificationPage.jsx"));
const ForgotPasswordPage = lazy(() => import("./pages/verification/ForgotPasswordPage.jsx"));
const ResetPasswordPage = lazy(() => import("./pages/verification/ResetPasswordPage.jsx"));
const TermsOfServicePage = lazy(() => import("./pages/legal/TermsOfServicePage.jsx"));
const PrivacyPolicyPage = lazy(() => import("./pages/legal/PrivacyPolicyPage.jsx"));
const HowItWorksPage = lazy(() => import("./pages/about-us/HowItWorksPage.jsx"));
const PartnerPage = lazy(() => import("./pages/partners/PartnerPage.jsx"));
const PartnerApplicationPage = lazy(() => import("./pages/partners/PartnerApplicationPage"));
const PartnerSuccessPage = lazy(() => import("./pages/partners/PartnerSuccessPage"));
const SupportPage = lazy(() => import("./pages/support/SupportPage"));
const VaccinationPage = lazy(() => import("./pages/vaccination/VaccinationPage"));
const CancellationPolicyPage = lazy(() => import("./pages/legal/CancellationPolicyPage.jsx"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage.jsx"));
const BlogIndexPage = lazy(() => import("./pages/blog/BlogIndexPage.jsx"));
const BlogPostPage = lazy(() => import("./pages/blog/BlogPostPage.jsx"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage.jsx"));

// Initialize Google Analytics
ReactGA.initialize("G-SLCKYYY2H1");

const ProtectedRoute = ({ children }) => {
  const { authUser, checkAuth } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (authUser) {
    if (!authUser.isVerified) {
      return <Navigate to="/verify-email" />;
    }
  }

  return children;
};

function App() {
  const { checkAuth } = useAuthStore();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 5 sec delay to open modal
  useEffect(() => {
    // Check if modal was previously closed in this session
    const modalClosed = sessionStorage.getItem("welcomeModalClosed");

    if (!modalClosed) {
      const timer = setTimeout(() => {
        setIsModalOpen(true);
      }, 5000); // 5 seconds delay

      // Cleanup timer if component unmounts
      return () => clearTimeout(timer);
    }
  }, []);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    // Remember that modal was closed for this session
    sessionStorage.setItem("welcomeModalClosed", "true");
  };

  // Track page views when route changes
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: location.pathname });
  }, [location]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const shouldHideFooter = location.pathname === "/messages";
  const shouldHideWhatsAppButton = location.pathname === "/messages";
  // Only show banner on home page
  const shouldShowBanner = location.pathname === "/";

  const { data: authUser, isLoading } = useQuery({
    queryKey: ["authUser"],
    queryFn: async () => {
      try {
        const res = await fetch("/api/auth/me");
        if (res.status === 401) {
          // Handle unauthorized silently (user is logged out)
          return null;
        }

        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || "Something went wrong");
        }
        return data;
      } catch (error) {
        // Only log errors that aren't related to authorization
        if (!error.message.includes("Unauthorized")) {
          console.error("Error fetching authUser:", error);
        }
        return null;
      }
    },
    retry: false,
    staleTime: 300000, // 5 minutes
    cacheTime: 600000, // 10 minutes
  });

  if (isLoading) {
    return (
      <div className="h-screen flex justify-center items-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <HelmetProvider>
      <ScrollToTop />
      <div className="flex flex-col min-h-screen bg-gray-100">
        {/* Navbar Component */}
        <Navbar />

        <NotificationListener />

        {/* Offer Banner - Only show on home page */}
        {shouldShowBanner && (
          <div className="bg-gradient-to-r from-[#e98c55]/20 to-[#dfa981]/20 py-3 px-4 border-b border-[#e98c55]/10 shadow-sm">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 max-w-6xl mx-auto">
              {/* Main offer content */}
              <button
                className="text-[#dc763b] font-medium hover:text-[#d37845] transition-all duration-200 flex items-center bg-transparent border-none cursor-pointer group"
                onClick={() => setIsModalOpen(true)}
              >
                <span className="text-center sm:text-left">
                  <span className="hidden sm:inline text-base">
                    🎉 First time here? Jom, enjoy RM20 OFF your first booking!
                  </span>
                  <span className="sm:hidden text-sm">
                    🎉 Get RM20 OFF your first booking!
                  </span>
                </span>
                <svg
                  className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5l7 7-7 7"
                  ></path>
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Welcome Modal with animation */}
        <WelcomeModal isOpen={isModalOpen} onClose={handleCloseModal} />

        {/* Routes */}
        <main className="flex-grow">
          <Suspense
            fallback={
              <div className="h-screen flex justify-center items-center">
                <LoadingSpinner size="lg" />
              </div>
            }
          >
          <Routes>
            <Route
              path="/"
              element={
                authUser ? (
                  <ProtectedRoute>
                    <HomePage />
                  </ProtectedRoute>
                ) : (
                  <HomePage />
                )
              }
            />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/blog" element={<BlogIndexPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/partners" element={<PartnerPage />} />
            <Route
              path="/partner-application"
              element={<PartnerApplicationPage />}
            />
            <Route path="/partner-success" element={<PartnerSuccessPage />} />
            <Route path="/help-centre" element={<HelpCenterPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route
              path="/login"
              element={
                !authUser ? (
                  <LoginPage />
                ) : authUser?.isVerified ? (
                  <Navigate to="/" />
                ) : (
                  <Navigate to="/verify-email" />
                )
              }
            />

            <Route
              path="/signup"
              element={
                !authUser ? (
                  <SignUpPage />
                ) : authUser?.isVerified ? (
                  <Navigate to="/" />
                ) : (
                  <Navigate to="/verify-email" state={{ authUser: authUser }} />
                )
              }
            />

            <Route
              path="/verify-email"
              element={
                !authUser ? (
                  <Navigate to="/login" />
                ) : authUser?.isVerified ? (
                  <Navigate to="/" />
                ) : (
                  <EmailVerificationPage />
                )
              }
            />

            <Route
              path="/forgot-password"
              element={!authUser ? <ForgotPasswordPage /> : <Navigate to="/" />}
            />
            <Route
              path="/reset-password/:token"
              element={!authUser ? <ResetPasswordPage /> : <Navigate to="/" />}
            />

            <Route path="/no-results" element={<NoResults />} />
            <Route path="/results" element={<ResultPage />} />
            <Route
              path="/hotel/:state/:city/:hotelName/:id"
              element={<HotelPage authUser={authUser} />}
            />

            <Route
              path="/admin-dashboard"
              element={
                authUser?.role === "admin" ? (
                  <AdminDashboard />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/owner-dashboard"
              element={
                authUser?.role === "owner" ? (
                  <OwnerDashboard />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/account-page"
              element={
                authUser?.role === "user" ? (
                  <AccountPage />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/purchase-success"
              element={
                authUser ? <PurchaseSuccessPage /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/purchase-cancel"
              element={
                authUser ? <PurchaseCancelPage /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/purchase-error"
              element={
                authUser ? <PurchaseErrorPage /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/booking-summary"
              element={
                authUser ? (
                  !authUser.isVerified ? (
                    <Navigate to="/verify-email" />
                  ) : (
                    <SummaryPage authUser={authUser} />
                  )
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/reservation-expired"
              element={
                authUser ? <ReservationExpired /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/messages"
              element={
                authUser ? (
                  <MessagesPage authUser={authUser} />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route path="/vaccination" element={<VaccinationPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/cancellation" element={<CancellationPolicyPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </Suspense>
        </main>

        {/* Footer */}
        {!shouldHideFooter && <Footer />}

        {!shouldHideWhatsAppButton && <WhatsAppButton />}

        <Toaster />
      </div>
    </HelmetProvider>
  );
}

export default App;